import React, { useRef, useState, useEffect, useContext } from 'react';
import { useTheme } from 'ThemeContext';
import { isBrowser, isMobile } from 'react-device-detect';
import { withRouter } from 'react-router-dom';
import { useWindowSize } from '@react-hook/window-size';
import { ReactSVG } from 'react-svg';
import { AppSettingsContext } from 'AppSettingsContext';

import Megamenu from './Megamenu';

import {
  Wrapper,
  Content,
  Item,
  Link,
  LinkExternal,
  Option,
  ItemContent,
  Arrow
} from './MenuList.style';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

function MenuList({ menu = [], toggle = false, location }) {
  const [width] = useWindowSize();
  const [itemsOpenedIndex, setItemsOpenedIndex] = useState([]);
  const [itemsLeftIndex, setItemsLeftIndex] = useState([]);
  const closeMenuTimeout = useRef(false);
  const theme = useTheme();
  const itemsRef = useRef([]);
  const itemsContentRef = useRef([]);
  const [appSettings] = useContext(AppSettingsContext);
  const checkMenuPosition = index => {
    const menu = itemsContentRef.current && itemsContentRef.current[index];
    if (menu) {
      const bound = menu.getBoundingClientRect();
      if (bound.right + 15 >= width) {
        return index;
      }
    }
  };

  const openMenu = index => {
    if (isBrowser) {
      if (closeMenuTimeout && closeMenuTimeout.current) {
        clearTimeout(closeMenuTimeout.current);
      }
      const currentIndex = index.split('-');
      const itemsIndexToOpen = currentIndex.reduce(
        (array, currentItem, index) => {
          const newItem =
            array && array.length > 0
              ? `${array[array.length - 1]}-${currentItem}`
              : currentItem;
          return [...array, newItem];
        },
        []
      );

      setItemsOpenedIndex(itemsIndexToOpen);
    }
  };

  const toggleMenu = index => {
    if (isMobile) {
      const currentIndex = index.split('-');
      const itemsIndexToOpen = currentIndex.reduce(
        (array, currentItem, index) => {
          const newItem =
            array && array.length > 0
              ? `${array[array.length - 1]}-${currentItem}`
              : currentItem;
          if (
            currentIndex.length - 1 === index &&
            itemsOpenedIndex.includes(newItem)
          )
            return [...array];
          return [...array, newItem];
        },
        []
      );

      setItemsOpenedIndex(itemsIndexToOpen);
    }
  };

  const closeMenu = index => {
    if (isBrowser) {
      if (closeMenuTimeout && closeMenuTimeout.current)
        clearTimeout(closeMenuTimeout.current);
      const itemsIndexToOpen = itemsOpenedIndex.filter(
        itemIndex => itemIndex !== index
      );
      closeMenuTimeout.current = setTimeout(() => {
        setItemsOpenedIndex(itemsIndexToOpen);
      }, 500);
    }
  };

  useEffect(() => {
    const indexes = [];
    if (itemsOpenedIndex && itemsOpenedIndex.length > 0) {
      itemsOpenedIndex.forEach(index => {
        if (checkMenuPosition(index)) indexes.push(index);
      });
      setItemsLeftIndex(
        [...itemsLeftIndex, ...indexes].filter(
          (item, index, array) => array.indexOf(item) === index
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsOpenedIndex]);

  useEffect(() => {
    setItemsLeftIndex([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width]);

  const renderMenu = ({ items = [], index = undefined, isSubmenu = false }) => {
    const checkIsActiveChildren = item => {
      if (item.slug && location.pathname.endsWith(`/${item.slug}`)) return true;
      return item.data && item.data.length > 0
        ? item.data.reduce((currentState, item) => {
            if (!currentState) currentState = checkIsActiveChildren(item);
            return currentState;
          }, false)
        : false;
    };

    return (
      items &&
      items.map((item, key) => {
        const itemNewIndex = `${index ? `${index}-` : ''}${key}`;
        const isActive = checkIsActiveChildren(item);
        const isMegamenu =
          item.isMegamenu || item.show_mega_menu ? true : false;
        return (
          <Item
            theme={theme.menuList.item}
            ref={el => (itemsRef.current[itemNewIndex] = el)}
            key={itemNewIndex}
            isSubmenu={isSubmenu}
            isMegamenu={isMegamenu ? true : undefined}
            onMouseLeave={e => {
              e.preventDefault();
              closeMenu(itemNewIndex);
            }}
            onMouseEnter={e => {
              e.stopPropagation();
              openMenu(itemNewIndex);
            }}
            onClick={e => {
              e.stopPropagation();
              toggleMenu(itemNewIndex);
            }}
          >
            <>
              {(item.slug && /(http(s?)):\/\//gi.test(item.slug)) ||
              (item.url && /(http(s?)):\/\//gi.test(item.url)) ||
              item.url_outside ? (
                <LinkExternal
                  theme={
                    isSubmenu
                      ? item.data && item.data.length > 0
                        ? theme.menuList.subOption
                        : theme.menuList.subLink
                      : item.data && item.data.length > 0
                      ? theme.menuList.option
                      : theme.menuList.link
                  }
                  cardBackground={appSettings.theme === 4 ? true : false}
                  opened={
                    itemsOpenedIndex &&
                    itemsOpenedIndex.length > 0 &&
                    itemsOpenedIndex.includes(itemNewIndex)
                      ? itemsOpenedIndex.includes(itemNewIndex).toString()
                      : undefined
                  }
                  active={
                    window.location.href.split('?')[0] === item.slug
                      ? true
                      : undefined
                  }
                  href={item.slug || item.url || item.url_outside}
                  target={
                    item._blank || item.url_blank || item.url_outside_blank
                      ? '_blank'
                      : '' || item.target || ''
                  }
                  rel={
                    item.nofollow ||
                    item.url_nofollow ||
                    item.url_outside_nofollow
                      ? 'nofollow'
                      : '' || item.rel || ''
                  }
                >
                  {item.name}
                  {(!item.data || (item.data && item.data.length <= 0)) && (
                    <Arrow
                      theme={theme.menuList.arrow}
                      isSubmenu={isSubmenu ? true : undefined}
                    >
                      <FontAwesomeIcon icon={theme.menuList.arrow.faIcon} />
                    </Arrow>
                  )}
                </LinkExternal>
              ) : item.slug ? (
                <Link
                  theme={
                    isSubmenu
                      ? item.data && item.data.length > 0
                        ? theme.menuList.subOption
                        : theme.menuList.subLink
                      : item.data && item.data.length > 0
                      ? theme.menuList.option
                      : theme.menuList.link
                  }
                  cardBackground={
                    appSettings.theme === 4 && !isSubmenu ? true : false
                  }
                  opened={
                    itemsOpenedIndex &&
                    itemsOpenedIndex.length > 0 &&
                    itemsOpenedIndex.includes(itemNewIndex)
                      ? itemsOpenedIndex.includes(itemNewIndex).toString()
                      : undefined
                  }
                  active={isActive ? isActive.toString() : undefined}
                  smooth
                  to={
                    item.slug && (item.slug[0] === '/' || item.slug[0] === '#')
                      ? item.slug
                      : `/${item.slug}`
                  }
                >
                  {item.name}
                  {(!item.data || (item.data && item.data.length <= 0)) && (
                    <Arrow
                      theme={theme.menuList.arrow}
                      isSubmenu={isSubmenu ? true : undefined}
                    >
                      <FontAwesomeIcon icon={theme.menuList.arrow.faIcon} />
                    </Arrow>
                  )}
                </Link>
              ) : (
                <Option
                  theme={
                    isSubmenu ? theme.menuList.subOption : theme.menuList.option
                  }
                  opened={
                    itemsOpenedIndex &&
                    itemsOpenedIndex.length > 0 &&
                    itemsOpenedIndex.includes(itemNewIndex)
                      ? itemsOpenedIndex.includes(itemNewIndex).toString()
                      : undefined
                  }
                  active={isActive ? isActive.toString() : undefined}
                >
                  {item.name}
                </Option>
              )}
              {item.data && item.data.length > 0 ? (
                <>
                  {isMegamenu ? (
                    <Megamenu
                      data={item.data}
                      opened={
                        itemsOpenedIndex &&
                        itemsOpenedIndex.length > 0 &&
                        itemsOpenedIndex.includes(itemNewIndex)
                          ? itemsOpenedIndex.includes(itemNewIndex).toString()
                          : undefined
                      }
                    />
                  ) : (
                    <ItemContent
                      ref={el => (itemsContentRef.current[itemNewIndex] = el)}
                      theme={theme.menuList.itemContent}
                      isSubmenu={isSubmenu}
                      opened={
                        itemsOpenedIndex &&
                        itemsOpenedIndex.length > 0 &&
                        itemsOpenedIndex.includes(itemNewIndex)
                          ? itemsOpenedIndex.includes(itemNewIndex).toString()
                          : undefined
                      }
                      leftSideDisplay={
                        itemsLeftIndex &&
                        itemsLeftIndex.length > 0 &&
                        itemsLeftIndex.includes(itemNewIndex)
                          ? itemsLeftIndex.includes(itemNewIndex).toString()
                          : undefined
                      }
                    >
                      {renderMenu({
                        items: item.data,
                        index: itemNewIndex,
                        isSubmenu: true
                      })}
                    </ItemContent>
                  )}
                </>
              ) : (
                ''
              )}
            </>
          </Item>
        );
      })
    );
  };

  return (
    <Wrapper theme={theme.menuList.wrapper}>
      {theme.menuList.wrapper.shape && (
        <ReactSVG className='shape' src={theme.menuList.wrapper.shape} />
      )}
      <Content
        theme={theme.menuList.content}
        toggle={toggle ? true : undefined}
      >
        {menu && menu.length > 0 ? renderMenu({ items: menu }) : ''}
      </Content>
    </Wrapper>
  );
}

export default withRouter(MenuList);
